import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Tags } from 'lucide-react';
import type { AttributeCategoryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useAttributeCategoriesList, useAttributeCategoryMutations } from '~/hooks/use-attribute-categories';
import { tokenToForm, type AttributeCategoryFormValues } from '~/lib/schemas/attribute-category';
import { AttributeCategoryEditDialog } from './attribute-category-edit-dialog';

/** Attribute Categories CRUD surface — parity with the legacy admin grid (Code/Name/Visible/Sort). */
export function AttributeCategoriesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useAttributeCategoriesList();
	const { remove } = useAttributeCategoryMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<AttributeCategoryFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<AttributeCategoryToken | null>(null);

	const columns = useMemo<SystemGridColumn<AttributeCategoryToken>[]>(
		() => [
			{
				key: 'name',
				header: t('attributes.categories.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: c => c.nm,
				exportValue: c => c.nm,
				render: c => <span className="truncate text-sm font-medium text-foreground">{c.nm}</span>
			},
			{
				key: 'visible',
				header: t('attributes.categories.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: c => (c.iv ? 1 : 0),
				exportValue: c => (c.iv ? t('common.yes') : t('common.no')),
				render: c => (c.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('attributes.categories.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: c => c.si ?? 0,
				exportValue: c => c.si ?? 0,
				render: c => <span className="text-sm tabular-nums text-foreground-muted">{c.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<AttributeCategoryToken>
				gridId="attribute-categories"
				title={t('attributes.categories.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={c => c.id ?? c.nm}
				columns={columns}
				searchPredicate={(c, q) => (c.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Tags size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('attributes.categories.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={c => {
					setEditing(tokenToForm(c));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="attribute-categories"
			/>

			<AttributeCategoryEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('attributes.categories.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('attributes.categories.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
